import * as React from 'react'
import styles from './card.module.scss'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const cardVariants = cva(
  styles.card,
  {
    variants: {
      orientation: {
        mainCategory: styles.mainCategory,
        horizontal: styles.horizontal,
      },
      colorScheme: {
        accent: styles.accent,
        dark: styles.dark,

      },
      effect: {
        default: '',
        'gray-gradient': styles.grayGradient,
      },
      size: {
        default: '',
        xs: styles.xs,
        xl: styles.xl,
      },
    },
    defaultVariants: {
      orientation: 'mainCategory',
      colorScheme: 'accent',
      effect: 'default',
      size: 'default',
    },
  },
)

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
      onClick?: () => void
    }

const Card = React.forwardRef<
  HTMLDivElement,
  CardProps
>(({ className, orientation, colorScheme, effect, size, onClick, ...props }, ref) => {

  return (
    <div
      ref={ref}
      className={cn(cardVariants({ orientation, colorScheme, effect, size, className }))}
      onClick={onClick}
      {...props}
    />
  )
})
Card.displayName = 'Card'

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(styles.header, className)}
    {...props}
  />
))
CardHeader.displayName = 'CardHeader'

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref}
       className={cn(styles.content, className)}
       {...props}
  />
))
CardContent.displayName = 'CardContent'

const CardBody = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(styles.body, className)}
    {...props}
  />
))
CardBody.displayName = 'CardBody'

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(styles.title, className)}
    {...props}
  />
))
CardTitle.displayName = 'CardTitle'

const CardSubtitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h4
    ref={ref}
    className={cn(styles.subtitle, className)}
    {...props}
  />
))
CardSubtitle.displayName = 'CardSubtitle'

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(styles.description, className, 'tw-mt-8 sm:tw-mt-2')}
    {...props}
  />
))
CardDescription.displayName = 'CardDescription'

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('tw-mt-6', styles.footer, className)}
    {...props}
  />
))
CardFooter.displayName = 'CardFooter'

export { Card, CardHeader, CardBody, CardFooter, CardTitle, CardSubtitle, CardDescription, CardContent }
